import { useState, useRef, useEffect } from "react";
import "../Style/Uiuxdesign.css";

import swotImage from "../assets/swot.png";
import flowImage from "../assets/flow.png";
import wireframesImage from "../assets/wireframes.png";
import dashboardImage from "../assets/dashboard.png";
import uxUiImage from "../assets/ui-ux.png";
import LetsBuild from "./LetsBuild";

const steps = [
  {
    label: "STEP  1",
    title: "User Research & UX Discovery",
    description:
      "Every successful digital product begins with understanding the people who will use it. Our UX research process uncovers user behaviors, business objectives, market opportunities, and customer pain points through competitor analysis, stakeholder workshops, surveys, interviews, and usability insights.By transforming real user data into actionable design strategies, we create intuitive digital experiences that improve engagement, increase customer satisfaction, and reduce development risks before design begins.",
  },
  {
    label: "STEP  2",
    title: "User Flow & Information Architecture",
    description:
      "Once research is complete, we design logical user flows and information architecture that guide users effortlessly through every interaction. From customer journeys and navigation structures to wireflows and task mapping, every screen is planned to deliver a seamless user experience.Our UX specialists eliminate unnecessary friction, simplify complex processes, and create intuitive pathways that increase usability, improve conversions, and ensure every interaction feels natural across web and mobile applications.",
  },
  {
    label: "STEP  3",
    title: "Wireframing & UI Design",
    description:
      "With the user journey and information architecture finalized, we create detailed wireframes that define the structure, layout, and functionality of every screen. These blueprints establish a clear visual hierarchy while ensuring effortless navigation and intuitive user interactions.Our UI designers then transform approved wireframes into visually engaging interfaces that reflect your brand identity, improve usability, and deliver a consistent experience across desktop, tablet, and mobile devices.",
  },
  {
    label: "STEP  4",
    title: "Interactive Prototyping & UX Validation",
    description:
      "Before development begins, we build interactive prototypes that simulate real user interactions, allowing stakeholders to experience the product before a single line of code is written. This process helps identify usability issues, optimize workflows, and validate design decisions early.Through usability testing, stakeholder feedback, and iterative improvements, we refine every interface to ensure exceptional user experiences, higher engagement, faster development cycles, and a seamless transition from design to development.",
  },
  {
    label: "STEP  5",
    title: "UI Development & Design Implementation",
    description:
      "After the design is approved, our developers transform every UI screen into clean, responsive, and high-performance code that works flawlessly across desktops, tablets, and mobile devices. We follow modern development standards to ensure pixel-perfect implementation, fast loading speeds, accessibility, and cross-browser compatibility.Every component is optimized for scalability, maintainability, and seamless integration with your development framework, ensuring your digital product is ready for launch with exceptional performance and user experience.",
  },
];

const allImages = [swotImage, flowImage, wireframesImage, dashboardImage, uxUiImage];
const TOTAL = steps.length;
const mod = (n, m) => ((n % m) + m) % m;

export default function UiuxDesignSection() {
  const [active, setActive] = useState(0);
  const [slideDir, setSlideDir] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);

  const navigate = (dir) => {
    if (isAnimating) return;
    const next = dir === "next" ? mod(active + 1, TOTAL) : mod(active - 1, TOTAL);
    setSlideDir(dir);
    setIsAnimating(true);
    setActive(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsAnimating(false);
      setSlideDir("");
    }, 460);
  };

  const goTo = (idx) => {
    if (isAnimating || idx === active) return;
    const dir = idx > active ? "next" : "prev";
    setSlideDir(dir);
    setIsAnimating(true);
    setActive(idx);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsAnimating(false);
      setSlideDir("");
    }, 460);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const step = steps[active];
  const img0 = allImages[mod(active, TOTAL)];
  const img1 = allImages[mod(active + 1, TOTAL)];
  const img2 = allImages[mod(active + 2, TOTAL)];
  const animCls = isAnimating ? ` hwd-slide--${slideDir}` : "";

  return (
    <section className="hwd-section">

      {/* ── Header ── */}
      <div className="hwd-header">
        <h2 className="hwd-title">How We Design</h2>
        <p className="hwd-subtitle">Simple Design. Smart Experience. Strong Impact.</p>
      </div>

      {/* ── Body ── */}
      <div className="hwd-body">

        {/* Left: step copy — boxed premium card */}
        <div className={`hwd-copy hwd-copy--card${isAnimating ? " hwd-copy--fade" : ""}`}>
          <span className="hwd-step-badge">{step.label}</span>
          <h3 className="hwd-step-title">{step.title}</h3>
          <p className="hwd-step-desc">{step.description}</p>
        </div>

        {/* Right: frames + controls */}
        <div className="hwd-right">
          <div className="hwd-stage">

            {/* ══ FRAME 3 — Furthest back (no buttons/notch) ══ */}
            <div className="hwd-frame hwd-frame--ghost">
              <div className="hwd-frame--ghost-inner">
                <img
                  key={`f3-${active}`}
                  src={img2}
                  alt=""
                  className={`hwd-screen-img${animCls}`}
                />
              </div>
            </div>

            {/* ══ FRAME 2 — Middle (no buttons/notch) ══ */}
            <div className="hwd-frame hwd-frame--flat">
              <div className="hwd-frame--flat-inner">
                <img
                  key={`f2-${active}`}
                  src={img1}
                  alt=""
                  className={`hwd-screen-img${animCls}`}
                />
              </div>
            </div>

            {/* ══ FRAME 1 — CSS Phone Shape (front) ══ */}
            <div className="hwd-frame hwd-frame--phone">
              <div className="hwd-phone-outer">
                <div className="hwd-phone-notch" />
                <div className="hwd-phone-screen">
                  <img
                    key={`f1-${active}`}
                    src={img0}
                    alt={step.title}
                    className={`hwd-screen-img${animCls}`}
                  />
                </div>
                <div className="hwd-phone-home" />
              </div>
            </div>

          </div>

          {/* ── Controls ── */}
          <div className="hwd-controls">
            <button className="hwd-arr" onClick={() => navigate("prev")} aria-label="Previous">‹</button>
            <div className="hwd-dots">
              {steps.map((_, i) => (
                <button
                  key={i}
                  className={`hwd-dot${i === active ? " hwd-dot--on" : ""}`}
                  onClick={() => goTo(i)}
                  aria-label={`Step ${i + 1}`}
                />
              ))}
            </div>
            <button className="hwd-arr" onClick={() => navigate("next")} aria-label="Next">›</button>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="hwd-cta">
        <button className="hwd-btn-primary">
          <LetsBuild />
          <span className="hwd-icon-flat">↗</span>
        </button>
        
      </div>

    </section>
  );
}